





























































































































































































































































import { Component, Prop, Mixins } from "vue-property-decorator";
import _ from "@/boot/lodash";

// Mixins
import BrandsStore from "@/mixins/brandsStore";

// Enums
import { DiscountTypes } from "@/data/enums/promotions";
import { ConditionTypes, ConditionApplies } from "@/data/enums/affiliates";
import { ConditionInvoiceCategories } from "@/data/affiliates";
import { FilterOperators } from "@/data/table";

// Types
import {
  IAffiliateTier,
  IAffiliateTierCondition,
  IAffiliateTierConditionForm
} from "@/models/affiliate";
import { IBillingTermsOption } from "@/models/constants";
import { ISelectedProduct } from "@/models/products";

@Component({
  components: {
    "multi-value-select": () =>
      import("@/components/app/global/fields/multiValueSelect.vue"),
    "condition-products-provider": () =>
      import(
        "@/components/app/admin/settings/affiliates/conditions/conditionProductsProvider.vue"
      ),
    "condition-products-table": () =>
      import(
        "@/components/app/admin/settings/affiliates/conditions/conditionProductsTable.vue"
      )
  }
})
export default class AddEditAffiliateTierConditionFields extends Mixins(
  BrandsStore
) {
  // Refs

  public declare $refs: {
    conditionProductsProvider: any;
  };

  // Props

  @Prop({ required: true })
  public value!: IAffiliateTierConditionForm;

  @Prop({ required: true })
  public affiliateTierCondition!: IAffiliateTierCondition | null;

  @Prop({ required: true })
  public selectedProducts!: ISelectedProduct[];

  @Prop({ required: true })
  public affiliateTierId!: IAffiliateTier["id"];

  @Prop({ required: true })
  public billingTermsOptions!: IBillingTermsOption[];

  @Prop({ default: false })
  public isDisabled!: boolean;

  // Data

  public formBillingTermsOptions: IBillingTermsOption[] = this.$_.concat(
    [
      {
        label: this.$t("_.all").toString(),
        value: FilterOperators.ALL
      }
    ],
    this.billingTermsOptions
  );
  public commissionApplies = ConditionApplies.ALL;

  public discountTypes = DiscountTypes;
  public conditionTypes = ConditionTypes;
  public invoiceCategories = ConditionInvoiceCategories;

  public get form() {
    return this.value;
  }

  public set form(value) {
    this.$emit("input", value);
  }

  // Lifecycle methods

  public async created() {
    this.initiateForm();
  }

  // Methods

  public initiateForm() {
    if (!this.isNewCondition) {
      this.form = _.merge(
        _.pick(this.affiliateTierCondition, [
          "active",
          "type",
          "amount",
          "condition_type",
          "billing_cycle_months",
          "commission_period",
          "commission_once_per_referral",
          "payout_interval",
          "invoice_categories",
          "apply_multiple_products",
          "product_price_order"
        ]) as IAffiliateTierConditionForm,
        this.affiliateTierCondition?.billing_cycle_months === null
          ? { billing_cycle_months: FilterOperators.ALL }
          : {}
      );

      this.$emit("initiateForm");
      this.setInitialCommissionAppliesValue();
    }

    if (
      this.form.billing_cycle_months !== FilterOperators.ALL &&
      !_.some(this.formBillingTermsOptions, [
        "value",
        this.form.billing_cycle_months
      ])
    ) {
      this.formBillingTermsOptions.push({
        value: this.form.billing_cycle_months as number,
        label: this.$t("_.every_months", {
          months: this.form.billing_cycle_months
        }).toString()
      });
    }
  }

  public manageSelectedProducts() {
    if (this.isDisabled) return;
    this.$refs.conditionProductsProvider.manageSelectedProducts(
      this.selectedProducts,
      {
        success: products => this.$emit("updateSelectedProducts", products)
      }
    );
  }

  public detachSelectedProducts(productIds) {
    this.$emit(
      "updateSelectedProducts",
      _.filter(
        this.selectedProducts,
        selectedProduct => !productIds.includes(selectedProduct.id)
      )
    );
  }

  public async onBillingTermsChange(value: any) {
    if (value !== "new") return;

    this.$store.dispatch("ui/open/windowModal", {
      config: {
        width: 480,
        component: () =>
          import(
            "@/components/app/global/fields/addCustomBillingTermModal.vue"
          ),
        props: {
          usedBillingTerms: _.map(
            this.formBillingTermsOptions,
            (billingTerm: { value: number; label: string }) => billingTerm.value
          )
        },
        events: {
          success: (result: number) => {
            this.formBillingTermsOptions.push({
              value: result,
              label: this.$t("_.every_months", { months: result }).toString()
            });

            this.$set(this.form, "billing_cycle_months", result);
          }
        }
      }
    });
  }

  public setInitialCommissionAppliesValue() {
    if (this.affiliateTierCondition?.apply_multiple_products) {
      this.commissionApplies = ConditionApplies.ALL;
      return;
    }

    this.commissionApplies = this.affiliateTierCondition?.product_price_order
      ? ConditionApplies.THE_CHEAPEST
      : ConditionApplies.THE_MOST_EXPENSIVE;
  }

  public changeCommissionApplies(value) {
    switch (value) {
      case ConditionApplies.ALL:
        this.form.apply_multiple_products = true;
        this.form.product_price_order = 0;
        return;
      case ConditionApplies.THE_CHEAPEST:
        this.form.apply_multiple_products = false;
        this.form.product_price_order = 1;
        return;
      case ConditionApplies.THE_MOST_EXPENSIVE:
        this.form.apply_multiple_products = false;
        this.form.product_price_order = 0;
        return;
    }
  }

  // Computed

  public get isNewCondition() {
    return !_.get(this.affiliateTierCondition, "id", false);
  }

  public get isMoneyBased() {
    return this.form.type === DiscountTypes.MONEY_BASED;
  }

  public get title() {
    return this.isNewCondition
      ? this.$t("_.add_affiliate_tier_condition")
      : this.$t("_.update_affiliate_tier_condition");
  }

  public get amount() {
    if (!this.isMoneyBased) {
      return `${this.form.amount}%`;
    }

    return `${_.get(this.brandCurrency, "prefix", "")}${
      this.form.amount
    }${_.get(this.brandCurrency, "suffix", "")}`;
  }

  public get forAllMessage() {
    return this.$t("_sentence.apply_commission_all_items", {
      value: this.amount
    });
  }

  public get forTheCheapestMessage() {
    return this.$t("_sentence.apply_commission_the_lowest", {
      value: this.amount
    });
  }

  public get forTheMostExpensiveMessage() {
    return this.$t("_sentence.apply_commission_the_highest", {
      value: this.amount
    });
  }

  public get commissionAppliesTypes() {
    return [
      {
        value: ConditionApplies.ALL,
        label: this.forAllMessage
      },
      {
        value: ConditionApplies.THE_CHEAPEST,
        label: this.forTheCheapestMessage
      },
      {
        value: ConditionApplies.THE_MOST_EXPENSIVE,
        label: this.forTheMostExpensiveMessage
      }
    ];
  }
}
